<template>
  <div>
    <div class="top-bar" v-if="this.showSearch">
      <input @input="onSearchChanged()" v-model="dataSearchValue" placeholder="Suche">
    </div>
    <div class="ada-table-container">
      <table class="ada-table" :class="scroll ? 'scroll' : ''">
        <thead>
        <tr class="table-header-row">
          <th v-for="(col, i) in columnObjects" :key="col.viewName+'_'+i" @click="onColumnClick(col)"
              :style="{width: col.width, maxWidth: scroll ? col.width : col.maxWidth}"
              :class="{sticky: (stickyFirstCol && (i === 0))}">
            <div class="table-header" :style="col.styles" :class="{pointer: col.sortable}">
              <div class="table-header-text">
                {{ col.viewName }}
              </div>
              <span class="icon-sort-default"
                    :style="{color: 'white', fontSize: '0.8em', display: 'flex', marginTop: '2px'}"
                    v-if="col.sortable && col.sort === 0"></span>
              <span class="icon-sort-up"
                    :style="{color: 'white', fontSize: '0.8em', display: 'flex', marginTop: '2px'}"
                    v-if="col.sortable && col.sort === 1"></span>
              <span class="icon-sort-down"
                    :style="{color: 'white', fontSize: '0.8em', display: 'flex', marginTop: '2px'}"
                    v-if="col.sortable && col.sort === -1"></span>
            </div>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr class="table-row" v-for="(row, index) in viewData" :key="row.id">
          <template v-if="rowInsidePagination(index)">
            <template v-for="(col, index2) in columnObjects">
              <td v-html="transform(col.propName, row, index2)"
                  :key="col.propName+''+index2"
                  @click="rowClicked($event, row, col.propName, index)"
                  :style="{width: col.width, maxWidth: col.maxWidth}"
                  :class="{sticky: (stickyFirstCol && (index2 === 0))}">
              </td>
            </template>
          </template>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="pagination" v-if="showPagination">
      <slot></slot>
      <div class="paginationWrapper">
        <span @click="previousPage()" class="pagination-icon pagination-element icon-arrow-left"/>
        <div v-bind:class="{'page-active': currentPage === page}" class="pagination-element"
             v-for="page in paginationPages" v-bind:key="page"
             @click="pageChanged(page)">
          {{ pageRender(page) }}
        </div>
        <span @click="nextPage()" class="pagination-element pagination-icon icon-arrow-right"/>
      </div>
    </div>
    <div class="pagination" v-if="!showPagination && this.$slots.default">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "adaDatatable",
  props: {
    movable: {
      required: false,
      default: false
    },
    tableId: {
      required: false,
      type: String
    },
    saveSortState: {
      default: false,
      type: Boolean
    },
    scroll: {
      required: false,
      type: Boolean
    },
    columns: {
      required: true
    },
    data: {
      required: true
    },
    showSearch: {
      type: Boolean
    },
    searchValue: {
      type: String
    },
    sizeOfPage: {
      type: Number
    },
    transformers: {
      $each: {
        validator: function (value) {
          return value.propName && value.transform
        }
      }
    },
    disablePagination: {
      required: false,
      type: Boolean
    },
    tableFilters: {
      required: false,
      type: Array,
      default: function () {
        return []
      },
      $each: {
        validator: function (value) {
          return value.propName && value.allowedValues
        }
      }
    },
    stickyFirstCol: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      viewData: [],
      savedData: [],
      filteredData: [],
      columnObjects: [],
      dataSearchValue: '',
      paginationPages: [1],
      pageSize: 10,
      currentPage: 1,
      savedPage: 1,
      pageCount: 1,
      showPagination: true,
    }
  },
  mounted() {
    this.init();
  },
  watch: {
    data: function () {
      this.init();
    },
    searchValue: function () {
      this.dataSearchValue = this.searchValue;
      this.applyFilters();
      this.onSearchChanged();
    },
    tableFilters: {
      handler: function () {
        this.applyFilters();
        this.onSearchChanged();
      },
      deep: true
    }
  },
  computed: {
    isTrackinglist() {
      return this.$route.path.indexOf('trackinglist') !== -1;
    }
  },
  created() {
    this.$nextTick(() => {
      this.$emit('domReady');
    });
  },
  methods: {
    pageRender: function (page) {
      if (page === 'forward' || page === 'backward') {
        return '...';
      }
      return page;
    },
    getSaveColumnStateName: function (propName) {
      return `${this.tableId}:${propName}`;
    },
    init: function () {
      let vm = this;
      vm.savedData = this.data.slice();
      vm.viewData = this.data.slice();
      this.showPagination = vm.viewData.length > 10 && !this.disablePagination;
      if (!this.showPagination) {
        this.currentPage = 1;
      }
      this.pageSize = this.sizeOfPage || 10;
      this.columnObjects = [];
      this.setPagination();
      this.columns.forEach(col => {
        let savedState = localStorage.getItem(this.getSaveColumnStateName(col.propName));
        let sort = col.sort ?? 0;
        if (savedState) {
          sort = JSON.parse(savedState).sort;
        }
        vm.columnObjects.push({
          viewName: col.viewName,
          propName: col.propName,
          sort: sort,
          sortable: col.sortable || false,
          width: col.width ? col.width : "auto",
          maxWidth: col.maxWidth ? col.maxWidth : "",
          styles: col.styles ? col.styles : null
        });
        if (sort !== 0) {
          this.sortData(col.propName, sort);
        }
      });
    },
    transform: function (propName, row, tdIndex) {
      let value = row[propName];
      let transformer = this.getTransformer(propName);
      if (!transformer) {
        let moveButtons = '';
        if (tdIndex === 0 && this.movable) {
          moveButtons = "<div class=\"move\">\n" +
              "<span class=\"icon icon-sort-up\"></span>" +
              "<span class=\"icon icon-sort-down\"></span>" +
              "</div>"
        }
        return moveButtons + value;
      } else {
        return transformer.transform(value, row);
      }
    },
    getTransformer: function (propName) {
      if (typeof this.transformers === 'undefined') {
        return null;
      }
      let transformer = null;
      for (let i = 0; i < this.transformers.length; i++) {
        if (this.transformers[i].propName === propName) {
          transformer = this.transformers[i];
          break;
        }
      }
      return transformer;
    },
    rowClicked: function (event, row, prop, rowIndex) {
      if (event.target.className.indexOf('icon-sort-') !== -1) {
        if (event.target.classList.contains('icon-sort-up')) {
          this.moveUp(rowIndex);
        }
        if (event.target.classList.contains('icon-sort-down')) {
          this.moveDown(rowIndex);
        }
      }
      this.$emit('rowClicked', {
        row: row,
        prop: prop,
        target: event.target
      });
      event.stopPropagation();
    },
    moveUp(index) {
      this.$emit('moveUp', index);
    },
    moveDown(index) {
      this.$emit('moveDown', index);
    },
    setPagination: function (useSavedState = false) {
      this.pageCount = Math.ceil(this.viewData.length / this.pageSize);
      this.paginationPages = [];
      let backPointsUsed = false;
      let forwardPointsUsed = false;
      for (let i = 0; i < this.pageCount; i++) {
        let page = i + 1;
        if ((Math.abs(this.currentPage - page) <= 2) || i === 0 || i === this.pageCount - 1) {
          this.paginationPages.push(page);
        } else {
          if (page < this.currentPage && !backPointsUsed) {
            backPointsUsed = true;
            this.paginationPages.push('backward')
          }
          if (page > this.currentPage && !forwardPointsUsed) {
            forwardPointsUsed = true;
            this.paginationPages.push('forward')
          }
        }
      }
      if (this.pageCount >= this.savedPage && useSavedState) {
        this.currentPage = this.savedPage;
      }
    },
    rowInsidePagination: function (index) {
      return index >= ((this.currentPage - 1) * this.pageSize) && index < (this.pageSize * this.currentPage)
    },
    applyFilters: function () {
      this.filteredData = this.savedData.slice();
      for (let i = 0; i < this.tableFilters.length; i++) {
        let filter = this.tableFilters[i];
        if (filter.allowedValues.length > 0) {
          for (let j = 0; j < this.filteredData.length; j++) {
            let includes = false;
            if (Array.isArray(this.filteredData[j][filter.propName])) {
              let nrOfIncludes = 0;
              for (let k = 0; k < this.filteredData[j][filter.propName].length; k++) {
                for (let l = 0; l < filter.allowedValues.length; l++) {
                  if (filter.allowedValues[l].toLowerCase() === this.filteredData[j][filter.propName][k].toLowerCase()) {
                    //includes = true;
                    nrOfIncludes += 1;
                  }
                }
              }
              if (filter.mode && filter.mode === 'AND') {
                includes = nrOfIncludes === filter.allowedValues.length;
              } else {
                includes = nrOfIncludes > 0;
              }
            } else {
              if (filter.allowedValues.includes(this.filteredData[j][filter.propName])) {
                includes = true;
              }
            }
            if (!includes) {
              this.filteredData.splice(j, 1);
              j--;
            }
          }
        }
      }
      this.savedPage = this.currentPage;
      this.currentPage = 1;
      this.setPagination(true);
    },
    onSearchChanged: function () {
      this.viewData = this.filteredData.slice();
      if (this.dataSearchValue.length > 1) {
        for (let i = 0; i < this.viewData.length; i++) {
          let entry = this.viewData[i];
          let includes = false;
          for (let key in entry) {
            let asString = '';
            if (!isNaN(entry[key])) {
              asString = entry[key] + '';
            } else if (typeof entry[key] === 'string') {
              asString = entry[key];
            }
            if (asString.toLowerCase().includes(this.dataSearchValue.toLowerCase())) {
              includes = true;
            }
          }
          if (!includes) {
            this.viewData.splice(i, 1);
            i--;
          }
        }
      }
      this.savedPage = this.currentPage;
      this.currentPage = 1;
      this.setPagination(true);
    },
    pageChanged: function (page) {
      if (page === 'backward') {
        this.previousPage();
      } else if (page === 'forward') {
        this.nextPage();
      } else {
        this.setPage(page);
      }
    },
    nextPage: function () {
      let page = this.currentPage;
      page += 1;
      if (page > this.pageCount) {
        page = this.pageCount;
      }
      this.setPage(page);
    },
    previousPage: function () {
      let page = this.currentPage;
      page -= 1;
      if (page <= 0) {
        page = 1;
      }
      this.setPage(page);
    },
    setPage: function (page) {
      this.currentPage = page;
      this.setPagination();
    },
    saveColumnSort: function (col) {
      if (this.tableId) {
        localStorage.setItem(this.getSaveColumnStateName(col.propName), JSON.stringify(col));
      }
    },
    onColumnClick: function (col) {
      col.sort += 1;
      if (col.sort === 2) {
        col.sort = -1;
      }
      this.columnObjects.forEach(obj => {
        if (obj.viewName !== col.viewName) {
          obj.sort = 0;
          this.saveColumnSort(obj);
        }
      });
      if (this.saveSortState) {
        this.saveColumnSort(col);
      }
      this.sortData(col.propName, col.sort);
    },
    sortData: function (columnName, sort) {
      function compare(a, b) {
        let value1 = a[columnName.toLowerCase()];
        let value2 = b[columnName.toLowerCase()];
        if (!value1) {
          value1 = a[columnName];
        }
        if (!value2) {
          value2 = b[columnName];
        }
        if (typeof value1 === 'string') {
          value1 = value1.toLowerCase();
        }
        if (typeof value2 === 'string') {
          value2 = value2.toLowerCase();
        }
        let comparison = 0;
        if (value1 > value2) {
          comparison = 1;
        } else if (value1 < value2) {
          comparison = -1;
        }
        return comparison * sort;
      }

      this.viewData.sort(compare);
      this.filteredData.sort(compare);
      this.savedData.sort(compare);
    }
  }
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/variables.scss" as *;

table.scroll thead tr:after {
  content: '';
  overflow-y: scroll;
  visibility: hidden;
}

table.scroll thead th {
  flex: 1 auto;
  display: block;
}

table.scroll thead tr {
  display: flex;
}

table.scroll tbody tr {
  display: flex;
  -ms-word-break: break-all;
  word-break: break-all;
  word-break: break-word;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  hyphens: auto;
}

table.scroll td {
  width: 100%;
}

table.scroll tbody {
  display: block;
  width: 100%;
  overflow-y: auto;
  height: auto;
  max-height: 400px;
}

.ada-table-container {
  position: relative;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: bold;
}

.pagination-element {
  background-color: white;
  color: $darkGrey;
  margin: 0 2px;
  width: 50px;
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.pagination-icon {
  font-size: 18px;
}

.pagination-element:hover {
  background-color: #dce0e6 !important;
}

.pagination .pagination-icon.pagination-element:last-child {
  margin-right: 0;
}

.pagination .pagination-icon.pagination-element:first-child {
  margin-left: 4px;
}

.paginationWrapper {
  display: flex;
  flex-flow: row;
  height: 100%;
}

.page-active, .page-active:hover {
  color: white !important;
  background-color: $darkGrey !important;
}

.top-bar {
  width: 100%;
}

.ada-table {
  width: 100%;
  border-spacing: 0;
}

.table-header-row {
  background: rgb(215, 4, 113);
  background: linear-gradient(90deg, #da2567 0%, #b92972 50%, #752b77 100%);
  background: -webkit-linear-gradient(90deg, #da2567 0%, #b92972 50%, #752b77 100%); /* For Chrome 25 and Safari 6, iOS 6.1, Android 4.3 */
  background: -moz-linear-gradient(90deg, #da2567 0%, #b92972 50%, #752b77 100%); /* For Firefox (3.6 to 15) */
  background: -o-linear-gradient(90deg, #da2567 0%, #b92972 50%, #752b77 100%); /* For old Opera (11.1 to 12.0) */
  background: linear-gradient(90deg, #da2567 0%, #b92972 50%, #752b77 100%); /* Standard syntax; must be last */
}

.table-header-row th:last-child {
  width: 10%;
}

.table-header-text {
  margin-right: 8px;
}

::v-deep .move {
  position: absolute;
  top: 0;
  left: 0px;
  display: flex;
  flex-flow: column;
  height: 36px;
  justify-content: space-between;
  font-size: 12px;
  color: $grey;
  padding: 4px 3px 1px 3px;
  margin-top: 2px;
  cursor: pointer;
}

.table-header {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 4px 18px 4px 4px;
  user-select: none;
  margin: 9px 0px 9px 15px;
  color: white;
  font-size: 15px;

  &.smallerMargin {
    padding: 0px 18px 0px 4px;
    margin: 8px 0px 8px 15px;
  }
}

.table-header.pointer {
  cursor: pointer;
}

th:not(:last-child) .table-header {
  border-right: 2px dotted white;
}

tr {
  width: 100%;
}

td {
  padding: 10px;
  position: relative;
}

tr:nth-child(odd) {
  background-color: $oddTableRow;
}

tr:nth-child(even) {
  background-color: white;
}

.table-row td {
  text-align: left;
  padding: 10px 20px;
  font-size: 15px;
}

.table-row {
  cursor: pointer;
}

.table-row:hover {
  background-color: $lightGrey;
  //color: white;
}

@media screen and (min-width: 1025px) {
  .sticky {
    position: sticky;
    left: 0;
    background: linear-gradient(90deg, #da2567 0%, #da2567 92%, #da256730 100%);
  }
}
</style>